import { Component, OnInit, AfterViewInit, OnDestroy, Inject, ViewChild, ElementRef } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { RouteTrackingService } from '../../services/route-tracking.service';
import { SpinnerService } from '../../services/spinner.service';
import { ErrorService } from '../../services/error.service';
import { ValidatorService } from '../../services/validator.service';
import { User, usernameAsString } from '../../models/user';
import { UserService } from '../../services/user.service';
import { OrderService } from '../../services/order.service';
import { SubscriptionService } from '../../services/subscription.service';
import { TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs/operators';

@Component({
  selector: 'app-email-route-shipping-labels-modal',
  templateUrl: './email-route-shipping-labels-modal.component.html',
  styleUrls: ['./email-route-shipping-labels-modal.component.css']
})
export class EmailRouteShippingLabelsModalComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('emailInput') emailInput: ElementRef;
  form: UntypedFormGroup;
  public subscriptions: any[] = [];
  public user: User = new User();
  public shippingLabels: any[] = [];
  public checkbox1Text: string = '';
  public checkbox2Text: string = '';
  public isBulkOrders: boolean = false;

  constructor(
    private _formBuilder: UntypedFormBuilder,
    private _dialogRef: MatDialogRef<EmailRouteShippingLabelsModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private _validatorService: ValidatorService,
    private _spinnerService: SpinnerService,
    private _errorService: ErrorService,
    private _routeTrackingService: RouteTrackingService,
    private _userService: UserService,
    private _orderService: OrderService,
    private _subscriptionService: SubscriptionService,
    private _translateService: TranslateService
  ) {}

  ngOnInit() {
    this.isBulkOrders = this.data.mode === 'bulkOrders';

    let defaultSubject: string;
    let defaultMessage: string;

    if (this.isBulkOrders) {
      const today = new Date();
      const formattedDate = `${today.getMonth() + 1}/${today.getDate()}/${today.getFullYear()}`;
      defaultSubject = `Report Shipping Labels - ${formattedDate}`;
      defaultMessage = `<user> has sent you shipping labels. If you have any questions, please contact us.`;
      this.checkbox1Text = this._translateService.instant('emailShippingLabelView.emailToOrdersCheckboxText');
      this.checkbox2Text = this._translateService.instant('emailShippingLabelView.emailToSpecifiedOrdersCheckboxText');
    } else {
      defaultSubject = `Shipping label for Route ${this.data.routeId} on Post date ${this.data.postDate}`;
      defaultMessage = `<user> has sent you a shipping label for Route ${this.data.routeId} on Post date ${this.data.postDate}. If you have any questions, please contact your carrier.`;
      this.checkbox1Text = this._translateService.instant('emailShippingLabelView.emailToStopsCheckboxText');
      this.checkbox2Text = this._translateService.instant('emailShippingLabelView.emailToSpecifiedStopsCheckboxText');
    }

    // Generic form structure with validation - following existing email modal pattern
    this.form = this._formBuilder.group({
      email: ['', [Validators.email]],             // Single email input (used by email chips component)
      emails: [[], [this._validatorService.minArrayLength(1)]], // Email chips array with validation
      emailToStops: [true, []],                    // Checkbox 1 - checked by default
      emailToSpecified: [false, []],               // Checkbox 2 - unchecked by default
      selectedShippingLabel: [null, [Validators.required]],
      subject: [defaultSubject, [Validators.required]], // Dynamic subject with route ID and post date
      message: [defaultMessage, [Validators.required]]  // Dynamic message with user, route ID, post date and contact instruction
    });

    // Setup conditional validation and field enabling/disabling
    this.setupFormValidation();

    this._userService.user$.pipe(take(1)).subscribe((data) => {
      this.user = data;
      defaultMessage = defaultMessage.replace('<user>', this.user?.friendlyName || this.user?.inetUser?.friendlyName || usernameAsString(this.user));
      this.form.get('message').setValue(defaultMessage);
      this.getAvailableShippingLabels();
    });
  }

  ngOnDestroy() {
    this._subscriptionService.unSubscribe(this.subscriptions);
  }

  getAvailableShippingLabels() {
    const context = this.isBulkOrders ? 'bulkOrder' : 'route';
    this._orderService.getAvailableShippingLabels(context).subscribe(response => {
      if (response && response.length > 0) {
        this.shippingLabels = response;
        const defaultLabelId = this.isBulkOrders
          ? null
          : this.user?.inetUser?.routeShippingLabelId;
        const defaultLabel = defaultLabelId
          ? response.find(l => l.procedureId === defaultLabelId)
          : null;
        this.form.controls.selectedShippingLabel.setValue(
          defaultLabel ? defaultLabel.procedureId : response[0].procedureId
        );
        this.form.updateValueAndValidity();
      }
    }, error => {
      this._errorService.error(error, 'getAvailableShippingLabels Failed', 'modal');
    });
  }

  setupFormValidation() {
    // Watch checkbox changes to clear chips and enable/disable fields
    this.form.get('emailToSpecified').valueChanges.subscribe((value) => {
      if (value) {
        // Enable email fields when checked
        this.form.get('emails').enable();
        this.form.get('email').enable();
      } else {
        // Clear and disable email fields when unchecked
        this.form.get('emails').setValue([]);
        this.form.get('email').setValue('');
        this.form.get('emails').disable();
        this.form.get('email').disable();
      }
    });

  }

  ngAfterViewInit() {
    // Set initial state after view is initialized to ensure EmailInputWithChipsComponent is loaded
    setTimeout(() => {
      const emailToSpecified = this.form.get('emailToSpecified').value;
      if (!emailToSpecified) {
        this.form.get('emails').disable();
        this.form.get('email').disable();
      }
    }, 0);
  }

  // Generic method to check if form is valid for submission
  isFormValid(): boolean {
    const emailToStops = this.form.get('emailToStops').value;
    const emailToSpecified = this.form.get('emailToSpecified').value;
    const emails = this.form.get('emails').value;

    // At least one checkbox must be checked
    const hasValidCheckbox = emailToStops || emailToSpecified;

    // If emailToSpecified is checked, emails array must have at least one email
    const hasValidEmails = !emailToSpecified || (emails && emails.length > 0);

    return hasValidCheckbox && hasValidEmails && this.form.valid;
  }

  // Generic send method
  send() {
    if (!this.isFormValid()) {
      return;
    }

    this._spinnerService.startSpinner();

    const emailData = {
      emailToStops: this.form.value.emailToStops,
      emailToSpecified: this.form.value.emailToSpecified,
      subject: this.form.value.subject,
      body: this.form.value.message,
      emails: this.form.value.emailToSpecified ? (this.form.value.emails || []) : []
    };

    const request$ = this.isBulkOrders
      ? this._orderService.bulkEmailOrderShippingLabels(
          this.data.reportData,
          emailData,
          this.form.value.selectedShippingLabel
        )
      : this._routeTrackingService.emailAllShippingLabels(
          this.data.routeId,
          this.data.postDate,
          emailData,
          this.form.value.selectedShippingLabel
        );

    request$.subscribe(() => {
      this._spinnerService.stopSpinner();
      this.close(true);
    }, error => {
      this._spinnerService.stopSpinner();
      this._errorService.error(error, 'emailShippingLabels Failed', 'modal');
    });
  }

  close(result: boolean = false) {
    this._dialogRef.close(result);
  }
}